import { readDomainRouterErrorMessage } from '../../state/domainRouterError';

const TRANSIENT_ASSISTANT_ERROR_PATTERNS = [
  /rate limit/i,
  /rate exceeded/i,
  /too many requests/i,
  /throttl/i,
  /timeout/i,
  /timed out/i,
  /temporarily unavailable/i,
  /connection reset/i,
  /network/i,
  /fetch/i,
  /status 429/i,
  /status 502/i,
  /status 503/i,
  /status 504/i,
] as const;

const delay = (ms: number): Promise<void> => new Promise((resolve) => {
  globalThis.setTimeout(resolve, ms);
});

export const isTransientAssistantRequestError = (error: unknown): boolean => {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return true;
  }
  const message = readDomainRouterErrorMessage(
    error,
    error instanceof Error ? error.message : String(error ?? ''),
  );
  if (!message) {
    return false;
  }
  return TRANSIENT_ASSISTANT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
};

export const runAssistantRequestWithTransientRetry = async <T>(
  operation: () => Promise<T>,
  input?: {
    maxAttempts?: number;
    retryDelayMs?: number;
    onRetry?: (input: {
      attempt: number;
      nextDelayMs: number;
      error: unknown;
    }) => void;
  },
): Promise<T> => {
  const maxAttempts = Math.max(1, input?.maxAttempts ?? 2);
  const retryDelayMs = Math.max(0, input?.retryDelayMs ?? 750);

  let attempt = 0;
  while (true) {
    try {
      return await operation();
    } catch (error) {
      attempt += 1;
      if (attempt >= maxAttempts || !isTransientAssistantRequestError(error)) {
        throw error;
      }
      input?.onRetry?.({
        attempt,
        nextDelayMs: retryDelayMs,
        error,
      });
      await delay(retryDelayMs);
    }
  }
};
